import React, { useState, useRef, useEffect } from 'react';
import Divider from '@mui/material/Divider';
import { TextField, Tooltip } from '@mui/material';
import TextIcon from './Freetext.svg';
import { makeStyles } from "@material-ui/core/styles";
import { LightenDarkenColor } from 'lighten-darken-color';
import Card from '@mui/material/Card';
import { Key, Save } from 'lucide-react';
import { toast } from 'react-toastify';

function FreeText(props) {
    const classes = useStyles();

    const titleRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
        const checkOverflow = () => {
            if (titleRef.current) {
                const element = titleRef.current;

                // Temporarily remove the clamp styles to measure natural height
                const originalStyle = element.style.webkitLineClamp;
                element.style.webkitLineClamp = "unset";
                element.style.display = "block";

                // Measure natural height
                const naturalHeight = element.getBoundingClientRect().height;

                // Restore original styles
                element.style.webkitLineClamp = originalStyle;
                element.style.display = "-webkit-box";

                // Get line height dynamically
                const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
                const maxHeight = lineHeight * 2; // Two lines

                setIsOverflowing(naturalHeight > maxHeight);
            }
        };

        checkOverflow();
        window.addEventListener("resize", checkOverflow);
        return () => window.removeEventListener("resize", checkOverflow);
    }, [props.description]);

    const titleElement = (
        <h2 ref={titleRef} className={classes.h2style}>
            {props.description}
        </h2>
    );

    function getRGB(c) {
        return parseInt(c, 16) || c
    }

    function getsRGB(c) {
        return getRGB(c) / 255 <= 0.03928
            ? getRGB(c) / 255 / 12.92
            : Math.pow((getRGB(c) / 255 + 0.055) / 1.055, 2.4)
    }

    function getLuminance(hexColor) {
        if (hexColor.includes('#')) {
            return (
                0.2126 * getsRGB(hexColor.substr(1, 2)) +
                0.7152 * getsRGB(hexColor.substr(3, 2)) +
                0.0722 * getsRGB(hexColor.substr(-2))
            )
        } else {
            return (
                0.2126 * hexColor +
                0.7152 * hexColor +
                0.0722 * hexColor
            )
        }

    }

    function getContrast(f, b) {
        const L1 = getLuminance(f)
        const L2 = getLuminance(b)
        return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05)
    }

    function getTextColor(bgColor) {

        if (bgColor === null) {
            return '#ffffff';
        } else {
            const whiteContrast = getContrast(bgColor, '#ffffff')
            const blackContrast = getContrast(bgColor, '#000000')
            return whiteContrast > blackContrast ? '#ffffff' : '#000000';

        }

    }

    const sendData = () => {
        if (props.text) {
            props.action(props.text, props.id);
        } else {
            toast.error("Failed to submit. Enter something in the inputfield.", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        }
    }

    return (
        <Card>
            <div className={classes.freeText} style={{
                color: props.textColor,
                backgroundColor: `${props.color}`
            }}>
                <div className={classes.headerDiv}>
                    {props.isKey && <Key className={classes.keyStyle}></Key>}
                    {isOverflowing ? (
                        <Tooltip title={props.description} placement="top" arrow>
                            {titleElement}
                        </Tooltip>
                    ) : (
                        titleElement
                    )}
                    <img className={classes.imageStyle} style={{ filter: props.darkness ? 'invert(1)' : 'contrast(80%)' }} src={TextIcon} alt="" />
                </div>
                <Divider></Divider>
                <div className={classes.textDiv} style={{
                    backgroundColor: LightenDarkenColor(props.color, 20)
                }}>
                    <TextField InputProps={{ style: { color: getTextColor(props.color) } }} onChange={(event) => props.setText(event.target.value, props.id)} autoFocus margin="dense" id={props.description} type="string" fullWidth value={props.text !== null ? props.text : ''} variant="standard" />
                </div>
                <Divider></Divider>
                <div className={classes.StyleDiv}>
                    <button className={`${classes.primaryButton} ${props.text === props.currentKey ? classes.disabledButton : ''}`} disabled={props.text === props.currentKey} onClick={() => sendData()} >
                        <Save className={classes.buttonIcon} />
                        Save
                    </button>
                </div>
            </div>
        </Card >
    );
}


export default FreeText;

const useStyles = makeStyles({

    freeText: {
        overflow: 'hidden',
        backgroundRepeat: "no-repeat",
        backgroundSize: "10rem 10rem",
        backgroundPosition: "center",
        height: "12rem",

    },

    headerDiv: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '33%',
    },

    h2style: {
        margin: '0',
        zIndex: '2',
        maxWidth: 'calc(100% - 150px)',

        // break words if they are too long
        whiteSpace: 'normal',
        textOverflow: 'ellipsis',
        wordWrap: 'break-word',

        // max two lines
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },

    imageStyle: {
        position: 'absolute',
        right: '3%',
        opacity: '50%'
    },

    keyStyle: {
        position: 'absolute',
        left: '3%',
        opacity: '50%',
        width: '50px'
    },

    textDiv: {
        paddingLeft: '15px',
        paddingRight: '15px',
        display: "flex",
        justifyContent: "center",
        height: '34%',
        alignItems: 'center',
    },

    StyleDiv: {
        height: '33%',
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-evenly",
        alignItems: 'center',
    },

    primaryButton: {
        height: "40px",
        width: "fit-content",
        padding: "0 16px",
        paddingLeft: "10px",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",

        border: "none",
        borderRadius: "4px",

        fontSize: "18px",
        color: "white",
        backgroundColor: "#C3996B",

        transition: "all 0.2s ease-in-out",

        "&:hover": {
            backgroundColor: "#a77d4f",
            cursor: "pointer",
        }
    },

    buttonIcon: {
        height: "20px",
        width: "20px",

        display: "block",
        strokeWidth: "1.8px",
    },

    disabledButton: {
        backgroundColor: "#DCDCDC !important",
        color: "#989898 !important",

        "&:hover": {
            cursor: "not-allowed",
        }
    },
});